import * as Api from '@ViewModels';

/** The three calendar layouts the user can switch between. */
export type CalendarViewMode = 'month' | 'week' | 'day';

export const CALENDAR_VIEW_MODES: CalendarViewMode[] = ['month', 'week', 'day'];

/** What a calendar item represents — a scheduled meeting, a hosted (registration) event, or an action item. */
export type CalendarItemKind = 'meeting' | 'event' | 'actionItem';

/**
 * A normalized item the calendar views render. Scheduled meetings and hosted events
 * are projected into this shape so grouping, overlap layout, and rendering can treat
 * them uniformly; the originating record is kept for click handling.
 */
export interface CalendarItem {
	kind: CalendarItemKind;
	id: string;
	title: string;
	start: Date;
	/** End time, or null for open-ended items (a default duration is applied for layout). */
	end: Date | null;
	/**
	 * All-day (no meaningful time-of-day). Rendered in the time grid's top all-day band
	 * instead of positioned in the hourly grid. Action items are all-day (due date only).
	 */
	allDay?: boolean;
	/** Accent color used for the block/dot/row. */
	color: string;
	/** Secondary label, e.g. the meeting status or "Event". */
	subLabel: string;
	/** Render muted/struck-through (e.g. cancelled meetings, completed action items). */
	muted: boolean;
	/** Render faded (reduced opacity) while keeping the type color — e.g. past meetings. */
	faded?: boolean;
	/** Past-due (incomplete) — flagged with an overdue cue rather than a different fill color. */
	overdue?: boolean;
	/**
	 * Has no due date and was placed on today as a convenience. Sorted after real dated items
	 * so genuine due-today work fills a day cell's visible slots first, with the rest collapsed
	 * into the "+N more" roll-up.
	 */
	undated?: boolean;
	/** Present when kind === 'meeting'. */
	meeting?: Api.IScheduledMeeting;
	/** Present when kind === 'event'. */
	event?: Api.IEventRegistrationSurvey;
	/** Present when kind === 'actionItem'. */
	actionItem?: Api.IActionItem;
}

/** Geometry for an item placed within a single day's time grid (week/day views). */
export interface IItemPosition {
	/** Minutes from the start of the day (00:00) to the item start, clamped to [0, 1440]. */
	startMinutes: number;
	/** Minutes from the start of the day to the item end, clamped to [startMinutes, 1440]. */
	endMinutes: number;
	/** Zero-based column index when items overlap. */
	column: number;
	/** Total number of columns in this item's overlap cluster. */
	columnCount: number;
}
